import { log } from '@/logger/logger';
import { getAxiosInstance } from '@/services/axiosConfig';
import type { ApiKeyProviderId } from '@/components/Settings/WorkspaceSettings/apiKeys/apiKeyProviders';

const logger = log.getLogger('byokExternalProviderConfigs.api');
const axiosInstance = getAxiosInstance();

const basePath = (workspaceId: string) => `/v1/workspaces/${workspaceId}/byok-external-provider-configs`;

export interface ServiceAccountResponseDto {
  clientEmail: string;
  projectId: string;
  /** Server returns this masked (e.g. suffix-only). */
  privateKey: string;
}

export interface BYOKExternalProviderConfigResponseDto {
  workspaceId: string;
  provider: string;
  isEnabled: boolean;
  /** Present for apiKey-based providers (e.g. fal). Masked by server (e.g. `*****abc123`). */
  apiKey?: string | null;
  /** Present for serviceAccount-based providers (e.g. google). */
  serviceAccount?: ServiceAccountResponseDto | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export type CreateByokBody =
  | { provider: ApiKeyProviderId; apiKey: string; isEnabled: boolean }
  | {
      provider: ApiKeyProviderId;
      serviceAccount: { clientEmail: string; projectId: string; privateKey: string };
      isEnabled: boolean;
    };

export type UpdateByokBody =
  | { apiKey?: string; isEnabled?: boolean }
  | { serviceAccount?: { clientEmail: string; projectId: string; privateKey: string }; isEnabled?: boolean };

export async function getByokExternalProviderConfigs(
  workspaceId: string,
): Promise<BYOKExternalProviderConfigResponseDto[]> {
  try {
    const { data } = await axiosInstance.get<BYOKExternalProviderConfigResponseDto[]>(basePath(workspaceId));
    return data;
  } catch (e) {
    logger.error('getByokExternalProviderConfigs failed', e);
    throw e;
  }
}

export async function createByokExternalProviderConfig(
  workspaceId: string,
  body: CreateByokBody,
): Promise<BYOKExternalProviderConfigResponseDto> {
  try {
    const { data } = await axiosInstance.post<BYOKExternalProviderConfigResponseDto>(basePath(workspaceId), body);
    return data;
  } catch (e) {
    logger.error('createByokExternalProviderConfig failed', e);
    throw e;
  }
}

export async function updateByokExternalProviderConfig(
  workspaceId: string,
  provider: ApiKeyProviderId,
  body: UpdateByokBody,
): Promise<BYOKExternalProviderConfigResponseDto> {
  try {
    const { data } = await axiosInstance.patch<BYOKExternalProviderConfigResponseDto>(
      `${basePath(workspaceId)}/${encodeURIComponent(provider)}`,
      body,
    );
    return data;
  } catch (e) {
    logger.error('updateByokExternalProviderConfig failed', e);
    throw e;
  }
}

export async function deleteByokExternalProviderConfig(workspaceId: string, provider: ApiKeyProviderId): Promise<void> {
  try {
    await axiosInstance.delete(`${basePath(workspaceId)}/${encodeURIComponent(provider)}`);
  } catch (e) {
    logger.error('deleteByokExternalProviderConfig failed', e);
    throw e;
  }
}
